import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = "reformity_access_token";
// PR-Cf — OWNER-only impersonation. When this localStorage key is set,
// the axios interceptor sends ``X-Impersonate-User-Id: <uid>`` on every
// request. The backend (api/rbac.py::get_scope) verifies the caller is
// an OWNER and reroutes the per-request RBAC scope to look like the
// target user's. Non-OWNERs that set this key in DevTools get a 403
// on their next call — defence-in-depth.
const IMPERSONATE_KEY = "reformity_impersonate_uid";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// PR-Cf — impersonation helpers.
export function getImpersonateUserId() {
  return localStorage.getItem(IMPERSONATE_KEY);
}

export function setImpersonateUserId(uid) {
  if (uid) localStorage.setItem(IMPERSONATE_KEY, uid);
  else localStorage.removeItem(IMPERSONATE_KEY);
}

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // PR-Cf — only attach the impersonation header when the caller has
  // an active session AND has explicitly opted in to impersonate.
  // The backend is the source of truth on whether the caller is
  // permitted to impersonate (OWNER-only), but stripping the header
  // for non-authed requests keeps the wire format clean.
  const impersonateUid = getImpersonateUserId();
  if (token && impersonateUid) {
    config.headers["X-Impersonate-User-Id"] = impersonateUid;
  }
  return config;
});

export function formatApiError(err) {
  if (!err) return "Something went wrong.";
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (detail == null) return err.message || "Request failed.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors look like:
    //   [{type, loc: ["body", "line_selections", 2, "amount"], msg, ...}]
    // Folding `loc` back into the message makes "Field required" on the
    // line_selections[2].amount field actually actionable for the
    // operator instead of a blind "Field required" toast.
    return detail
      .map((e) => {
        if (!e || typeof e !== "object") return JSON.stringify(e);
        const locPath = Array.isArray(e.loc)
          ? e.loc
              .filter((x) => x !== "body" && x !== "query" && x !== "path")
              .join(".")
          : "";
        const msg = typeof e.msg === "string" ? e.msg : JSON.stringify(e);
        return locPath ? `${locPath}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join(" • ");
  }
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  if (typeof detail === "object") {
    // HTTPException(status, {error: "...", reason: "..."}) style payloads.
    if (typeof detail.reason === "string" && typeof detail.error === "string") {
      return `${detail.error}: ${detail.reason}`;
    }
    if (typeof detail.error === "string") return detail.error;
  }
  if (status) return `Request failed (${status}).`;
  return String(detail);
}
